import {
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  Box,
  Container,
  Paper,
  Fade,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { AlertComponent, DialogComponent, NavBar } from "../../components";
import { useDate, useFetch } from "../../hooks";
import { TechListProps } from "../../interfaces/interfaces";
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SellIcon from "@mui/icons-material/Sell";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import DifferenceIcon from "@mui/icons-material/Difference";
import { Link, useNavigate } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import DataContext from "../../context/DataContext";
import PaginatorContext from "../../context/PaginatorContext";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../utils/dataGridStyles";

interface FetchResponse {
  success?: boolean;
  data: TechListProps[];
}

export const TallyDesignList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const { data, isLoading, fetchData, pagination } = useFetch<FetchResponse>(
    "tally-detail",
    paginationModel,
  );
  const { onResetValues, fetchDataTally, onDeleteTally, isSuccess, isError } =
    useContext(DataContext);
  const { getDateFromString } = useDate();
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const matches = useMediaQuery("(min-width:600px)");
  const [tableData, setTableData] = useState<TechListProps[]>([]);

  useEffect(() => {
    data && data.data && setTableData(data.data);
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "client",
      headerName: "Client",
      renderHeader: () => <strong>{"CLIENT"}</strong>,
      sortable: true,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.well.client.name || "",
      renderCell: (params: any) => {
        return (
          <Link
            to={`/tally/${params.row?.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {params.row?.well.client.name}
          </Link>
        );
      },
    },
    {
      field: "well",
      headerName: "Well",
      renderHeader: () => <strong>{"WELL"}</strong>,
      sortable: true,
      width: 180,
      valueGetter: (params: GridValueGetterParams) =>
        params.row?.well.name || "",
    },
    {
      field: "customName",
      headerName: "Description",
      renderHeader: () => <strong>{"DESCRIPTION"}</strong>,
      width: 180,
    },
    {
      field: "date",
      headerName: "Date",
      renderHeader: () => <strong>{"DATE"}</strong>,
      width: 110,
      valueGetter: (params: GridValueGetterParams) =>
        getDateFromString(params.row?.date),
    },
    {
      field: "action",
      headerName: "Actions",
      renderHeader: () => <strong>{"ACTIONS"}</strong>,
      width: 280,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <Box className="action-buttons" sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit">
              <IconButton
                onClick={() => navigate(`/tally/${params.row?.id}`)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate">
              <IconButton
                onClick={() => {
                  fetchDataTally(params.row?.id);
                  navigate("/tally/0");
                }}
                size="small"
                sx={modernIconButtonStyles(theme)}
              >
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Create as Technical Design">
              <IconButton
                onClick={() => {
                  fetchDataTally(params.row?.id, true);
                  navigate("/tech/0");
                }}
                size="small"
                sx={modernIconButtonStyles(theme)}
              >
                <DifferenceIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Generate Sales">
              <IconButton
                onClick={() => navigate(`/sales/tally/${params.row?.id}`)}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <SellIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton
                component={Link}
                to={`/tally/${params.row?.id}?pdf=true`}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                onClick={() => {
                  setIdToDelete(params.row?.id);
                  setIsOpenAlert(true);
                }}
                sx={modernIconButtonStyles(theme)}
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const buttons = [
    {
      title: "New",
      action: () => {
        onResetValues();
        navigate("/tally/0");
      },
      icon: <AddBoxIcon />,
    },
  ];
  return (
    <>
      <NavBar title="Tally & WBD Designs" buttons={buttons} />
      <Box
        sx={{
          marginTop: "80px",
          minHeight: "calc(100vh - 80px)",
          background:
            "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
          padding: { xs: 2, md: 4 },
        }}
      >
        <Container maxWidth="xl">
          <Fade in={true} timeout={600}>
            <Paper
              elevation={3}
              sx={{
                padding: { xs: 2, md: 4 },
                borderRadius: "20px",
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(140, 150, 169, 0.1)",
                boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
              }}
            >
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  sx={modernDataGridStyles(theme)}
                  density={matches ? "standard" : "compact"}
                  rows={tableData}
                  columns={columns}
                  slots={{ toolbar: GridToolbar }}
                  loading={isLoading}
                  rowCount={+pagination?.totalRecords}
                  paginationMode="server"
                  paginationModel={paginationModel}
                  onPaginationModelChange={fetchPaginationModel}
                />
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>
      {isSuccess && <AlertComponent type="success" />}
      {isError && <AlertComponent type="error" />}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this item?</div>}
          onAccept={() => {
            onDeleteTally(idToDelete);
            setIdToDelete("");
            setIsOpenAlert(false);
            fetchData();
          }}
          onCancel={() => {
            setIsOpenAlert(false);
            setIdToDelete("");
          }}
        />
      )}
    </>
  );
};
